const toCamelCase = (str) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const fetchEvents = async (groupAliasIDs) => {
  try {
    console.log('Fetching events for groups:', groupAliasIDs);
    const allData = await Promise.all(
      groupAliasIDs.map(async (groupAliasID) => {
        // Construct the target API URL parameters
        const targetParams = new URLSearchParams({
          groupAliasID: groupAliasID,
          key: 'events',
          limit: '50',
          linkingURL: `https://opensports.net/${groupAliasID}/tab/events?tags=Pickleball`
        });

        // Construct the full target API URL using the correct path
        // Define targetApiUrl *before* using it in proxyUrl
        const targetApiUrl = `https://osapi.opensports.ca/groups/layouts/tabs/listOne?${targetParams.toString()}`;

        // Construct the proxy URL, passing the target URL as an encoded parameter
        // Use the correct Netlify site URL
        const proxyUrl = `https://findpickleball.netlify.app/.netlify/functions/proxy?targetUrl=${encodeURIComponent(targetApiUrl)}`;
        console.log('Fetching from Proxy URL:', proxyUrl); // Log the full proxy URL
        console.log('Target API URL:', targetApiUrl); // Log the target API URL

        const response = await fetch(proxyUrl, { // Use the modified proxy URL
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text(); // Capture response body for debugging
          console.error(`HTTP error! status: ${response.status}, response: ${errorText}`);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`Received data for ${groupAliasID}:`, data);

        const groupName = toCamelCase(data.result?.group?.name || 'N/A');
        const twoLevelFilters = data.result?.twoLevelFilters || [];

        // Filter for pickleball events (sportID = 28)
        const pickleballEvents = (data.result?.data || []).filter(event => event.sportID === 28);

        const venueTags = twoLevelFilters.find((filter) => filter?.label === 'Venue')?.tags || [];
        const suburbTags = twoLevelFilters.find((filter) => filter?.label === 'Suburb')?.tags || [];

        const combinedTags = [...venueTags, ...suburbTags];

        return pickleballEvents.map((event) => {
          const {
            waitlistUserCount = 'N/A',
            aliasID,
            place: { title = 'N/A', address = 'N/A', latlng = {} } = {},
            start = 'N/A',
            end = 'N/A',
            ticketsSummary = [],
            maxAttendees = null,
            registeredAttendees = 'N/A',
            data: { level: { title: difficulty = 'N/A' } = {} } = {},
            tags = [],
            place: { city = 'N/A', title: placeTitle = 'N/A' } = {},
          } = event;

          const matchingTag = combinedTags.find((tag) =>
            Array.isArray(tags) && tags.some((eventTag) => eventTag === tag?.tag)
          );

          const venueOrSuburb = matchingTag
            ? matchingTag.label
            : city !== 'N/A'
            ? city
            : placeTitle;

          const freeSlots =
            maxAttendees !== null && maxAttendees !== undefined
              ? maxAttendees - registeredAttendees
              : 'uncapped/event?';

          const formattedMaxAttendees =
            maxAttendees !== null && maxAttendees !== undefined
              ? maxAttendees
              : '∞'; // Use infinity symbol for uncapped events

          const formatTime = (time) =>
            time !== 'N/A'
              ? new Date(time).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              : 'N/A';

          // Remove formatDate and durationHours as they're not used

          // Format start date as ISO string in AEST (Australia/Brisbane)
          const formattedStartDate = start !== 'N/A'
            ? (() => {
                // Parse UTC date, convert to Brisbane, extract YYYY-MM-DD
                const utcDate = new Date(start);
                const brisbaneDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
                // Pad month and day
                const yyyy = brisbaneDate.getFullYear();
                const mm = String(brisbaneDate.getMonth() + 1).padStart(2, '0');
                const dd = String(brisbaneDate.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
              })()
            : 'N/A'; // ISO date
          const formattedStart = formatTime(start);
          const formattedEnd = formatTime(end);

          const prices = ticketsSummary.map((ticket) => ticket.price);
          const minPrice = prices.length > 0 ? Math.min(...prices) : 'N/A';
          const maxPrice = prices.length > 0 ? Math.max(...prices) : 'N/A';
          const priceRange =
            minPrice !== 'N/A' && maxPrice !== 'N/A'
              ? `$${minPrice}-$${maxPrice}`
              : 'N/A';

          return {
            groupName: groupName,
            duration: start !== 'N/A' && end !== 'N/A'
              ? `${(Math.round(((new Date(end) - new Date(start)) / (1000 * 60 * 30)) / 2 * 2) / 2)}hr`
              : '',
            registeredAttendees,
            freeSlots,
            waitlistUserCount,
            url: `https://opensports.net/posts/${aliasID}`,
            location: title,
            address,
            coordinates: `${latlng.latitude || 'N/A'}, ${latlng.longitude || 'N/A'}`,
            startDate: formattedStartDate, // Use ISO date
            time: `${formattedStart} - ${formattedEnd}`,
            priceRange,
            difficulty,
            venue: venueOrSuburb,
            maxAttendees: formattedMaxAttendees, // Ensure maxAttendees is properly formatted
          };
        });
      })
    );

    return allData.flat().sort((a, b) => {
      const dateA = new Date(a.startDate + ' ' + a.time.split(' - ')[0]);
      const dateB = new Date(b.startDate + ' ' + b.time.split(' - ')[0]);
      return dateA - dateB;
    });
  } catch (error) {
    console.error('Error in fetchEvents:', error);
    throw error;
  }
};
